// Refer.js
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { APP_CONFIG } from "../app-config";
import SidebarHeader from "../components/SidebarHeader";
import { encryptDataHybrid, apiCall } from "../utils/api";
import toast, { Toaster } from 'react-hot-toast';
import { IoWarningOutline } from "react-icons/io5";
import { GoVerified, GoUnverified } from "react-icons/go";
import BottomNav from "../components/bottom-menu";
import { FiArrowLeft } from "react-icons/fi";

const COLORS = APP_CONFIG.colors;

const steps = [
  { num: 1, title: "Share your code", desc: "Share your unique referral code with friends and family" },
  { num: 2, title: "Friend signs up", desc: "Your friend creates an account using your referral code" },
  { num: 3, title: "Both earn rewards", desc: "You get ₹50 and your friend gets ₹25 wallet credits" },
];

function Refer() {
  const navigate = useNavigate();
  const [fetching, setFetching] = useState(true);
  const [referCode, setReferCode] = useState("");
  const [referrals, setReferrals] = useState([]);
  const [totalEarned, setTotalEarned] = useState(0);

  useEffect(() => {
    const fetchRefer = async () => {
      try {
        const result = await apiCall(
          "refer-fetch",
          { refer_code: "ABC123" },
          true
        );

        if (result.status) {
          setReferCode(result.refer_code || "ABC123");
          setReferrals(result.refer_user || []);

          const total = (result.refer_user || []).reduce((sum, ref) => sum + (parseInt(ref.balance) || 0), 0);
          setTotalEarned(total);
        } else {
          toast.error(result.message || "An error occurred. Please try again.");
        }
      } catch (error) {
        toast.error("Network error. Please try again.");
      } finally {
        setFetching(false);
      }
    };
    fetchRefer();
  }, []);

  const handleCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(referCode).then(() => {
        toast.success("Referral code copied!");
      }).catch(() => {
        toast.error("Failed to copy code");
      });
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `Join ${APP_CONFIG.app.name}`,
        text: `Use my referral code ${referCode} to sign up on ${APP_CONFIG.app.name} and get ₹25 bonus!`,
      }).catch(() => { });
    } else {
      handleCopy();
    }
  };

  if (fetching) {
    return (
      <SidebarHeader>
        <div className="flex items-center gap-3 pb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-200 hover:bg-gray-300 transition"
          >
            <FiArrowLeft className="text-xl" />
          </button>
          <h1 className="text-lg font-semibold text-gray-800">
            Refer & Earn
          </h1>
        </div>
        <div className="max-w-7xl mx-auto mb-16">
          <div className="text-center py-12">
            <div className="w-10 h-10 rounded-full animate-spin mx-auto mb-4" style={{ border: `3px solid ${COLORS.primaryColor}33`, borderTopColor: COLORS.primaryColor }} />
            <div className="text-sm font-medium" style={{ color: COLORS.textSecondary }}>
              Loading referral data...
            </div>
          </div>
        </div>
        <Toaster />
      </SidebarHeader>
    );
  }

  return (
    <SidebarHeader>
      <div className="flex items-center gap-3 pb-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-200 hover:bg-gray-300 transition"
        >
          <FiArrowLeft className="text-xl" />
        </button>
        <h1 className="text-lg font-semibold text-gray-800">
          Refer & Earn
        </h1>
      </div>
      <div className="max-w-7xl mx-auto mb-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left Column - Hero Card & How It Works */}
          <div className="lg:col-span-3">
            {/* Hero Card */}
            <div
              className="rounded-2xl p-6 mb-6 text-white animate-fadeIn"
              style={{
                background: `linear-gradient(135deg, ${COLORS.primaryColor}, ${COLORS.primaryDark})`,
                borderRadius: COLORS.borderRadius,
                boxShadow: `0 8px 24px ${COLORS.primaryColor}30`,
              }}
            >
              <h2 className="text-lg font-bold mb-2" style={{ color: COLORS.textOnPrimary }}>
                Earn ₹10 for every friend you refer!
              </h2>
              <p className="text-sm opacity-90 mb-4" style={{ color: COLORS.textOnPrimary }}>
                Share your unique code and earn rewards
              </p>

              {/* Referral Code Box */}
              <div
                className="rounded-xl p-4 text-center mb-4"
                style={{
                  background: `${COLORS.textOnPrimary}20`,
                  backdropFilter: "blur(10px)",
                }}
              >
                <div
                  className="text-xl font-bold tracking-wider"
                  style={{
                    fontFamily: "'Courier New', monospace",
                    color: COLORS.textOnPrimary,
                    letterSpacing: "4px",
                  }}
                >
                  {referCode}
                </div>
              </div>

              <div className="flex gap-2.5">
                <button
                  onClick={handleCopy}
                  className="flex-1 h-11 rounded-lg text-sm font-semibold cursor-pointer flex items-center justify-center gap-2 transition-all duration-200 active:scale-95"
                  style={{
                    background: "rgba(255,255,255,0.2)",
                    color: COLORS.textOnPrimary,
                    border: "1px solid rgba(255,255,255,0.3)",
                    borderRadius: COLORS.borderRadius,
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                  Copy Code
                </button>
                <button
                  onClick={handleShare}
                  className="flex-1 h-11 rounded-lg text-sm font-semibold cursor-pointer flex items-center justify-center gap-2 transition-all duration-200 active:scale-95"
                  style={{
                    background: COLORS.bgColor,
                    color: COLORS.primaryColor,
                    borderRadius: COLORS.borderRadius,
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="18" cy="5" r="3" />
                    <circle cx="6" cy="12" r="3" />
                    <circle cx="18" cy="19" r="3" />
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                  </svg>
                  Share
                </button>
              </div>
            </div>

            {/* How It Works */}
            <div className="mb-6">
              <h3
                className="text-base font-bold mb-4"
                style={{ color: COLORS.textPrimary }}
              >
                How It Works
              </h3>
              <div className="pl-2">
                {steps.map((step, index) => (
                  <div key={step.num} className="flex gap-3.5 relative pb-6 last:pb-0">
                    {index < steps.length - 1 && (
                      <div
                        className="absolute left-[15px] top-9 bottom-0 w-0.5"
                        style={{ background: COLORS.primaryLight }}
                      />
                    )}
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 z-[1]"
                      style={{
                        background: COLORS.primaryColor,
                        color: COLORS.textOnPrimary,
                      }}
                    >
                      {step.num}
                    </div>
                    <div>
                      <div
                        className="text-sm font-semibold mb-0.5"
                        style={{ color: COLORS.textPrimary }}
                      >
                        {step.title}
                      </div>
                      <div
                        className="text-xs leading-relaxed"
                        style={{ color: COLORS.textSecondary }}
                      >
                        {step.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Stats & Referrals */}
          <div className="lg:col-span-2">
            {/* Total Earned */}
            <div
              className="rounded-2xl p-4 mb-5 flex flex-col items-center border"
              style={{
                background: COLORS.surfaceColor,
                borderColor: COLORS.borderColor,
                borderRadius: COLORS.borderRadius,
              }}
            >
              <span
                className="text-sm font-medium mb-1"
                style={{ color: COLORS.textSecondary }}
              >
                Total Earned
              </span>
              <span
                className="text-3xl font-bold"
                style={{ color: COLORS.primaryColor }}
              >
                ₹{totalEarned}
              </span>
            </div>

            {/* My Referrals */}
            <div>
              <h3
                className="text-base font-bold mb-3 flex items-center justify-between"
                style={{ color: COLORS.textPrimary }}
              >
                My Referrals
                <span
                  className="text-sm font-medium px-2.5 py-0.5 rounded-full"
                  style={{
                    background: COLORS.primaryColor,
                    color: COLORS.textOnPrimary,
                  }}
                >
                  {referrals.length}
                </span>
              </h3>

              {referrals.length === 0 ? (
                <div
                  className="text-center py-8 px-4 rounded-2xl border"
                  style={{
                    background: COLORS.surfaceColor,
                    borderColor: COLORS.borderColor,
                    borderRadius: COLORS.borderRadius,
                  }}
                >
                  <div className="text-4xl mb-3">🤝</div>
                  <p className="text-sm" style={{ color: COLORS.textSecondary }}>
                    No referrals yet. Share your code to earn rewards!
                  </p>
                </div>
              ) : (
                <div
                  className="rounded-2xl overflow-hidden border"
                  style={{
                    background: COLORS.bgColor,
                    borderColor: COLORS.borderColor,
                    borderRadius: COLORS.borderRadius,
                  }}
                >
                  {referrals.map((ref, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-3 p-3"
                      style={{
                        borderBottom: idx < referrals.length - 1 ? `1px solid ${COLORS.borderColor}` : "none",
                      }}
                    >
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-base font-bold flex-shrink-0"
                        style={{
                          background: `${COLORS.primaryColor}15`,
                          color: COLORS.primaryColor,
                        }}
                      >
                        {ref.name ? ref.name.charAt(0).toUpperCase() : "U"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className="text-sm font-semibold truncate"
                          style={{ color: COLORS.textPrimary }}
                        >
                          {ref.name || "User"}
                        </div>
                        <div
                          className="text-xs truncate"
                          style={{ color: COLORS.textSecondary }}
                        >
                          {ref.mobile || "No mobile"}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span
                          className="text-[10px] font-semibold py-0.5 px-2 rounded"
                          style={{
                            color: (parseInt(ref.balance) || 0) > 0 ? COLORS.primaryColor : COLORS.textSecondary,
                            background: (parseInt(ref.balance) || 0) > 0 ? `${COLORS.primaryColor}15` : `${COLORS.borderColor}`,
                          }}
                        >
                          {(parseInt(ref.balance) || 0) > 0 ? "Active" : "Pending"}
                        </span>
                        {(parseInt(ref.balance) || 0) > 0 && (
                          <div
                            className="text-sm font-bold mt-0.5"
                            style={{ color: COLORS.primaryColor }}
                          >
                            +₹{ref.balance}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <Toaster />

        <style>
          {`
          .active\\:scale-95:active {
            transform: scale(0.95);
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.4s ease forwards;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .animate-spin {
            animation: spin 1s linear infinite;
          }
        `}
        </style>
      </div>
    </SidebarHeader>
  );
}

export default Refer;